/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import T from "i18n-react/dist/i18n-react";
import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
  createAction,
  stopLoading,
  startLoading,
  escapeFilterValue,
  fetchResponseHandler,
  fetchErrorHandler,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "openstack-uicore-foundation/lib/utils/actions";
import debounce from "lodash/debounce";
import URI from "urijs";
import { getAccessTokenSafely } from "../utils/methods";
import {
  DEBOUNCE_WAIT,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_ORDER_DIR,
  DEFAULT_PER_PAGE
} from "../utils/constants";

URI.escapeQuerySpace = false;

export const REQUEST_COMPANIES = "REQUEST_COMPANIES";
export const RECEIVE_COMPANIES = "RECEIVE_COMPANIES";
export const RECEIVE_COMPANY = "RECEIVE_COMPANY";
export const RESET_COMPANY_FORM = "RESET_COMPANY_FORM";
export const COMPANY_DELETED = "COMPANY_DELETED";
export const UPDATE_COMPANY = "UPDATE_COMPANY";
export const COMPANY_UPDATED = "COMPANY_UPDATED";
export const COMPANY_ADDED = "COMPANY_ADDED";
export const LOGO_ATTACHED = "LOGO_ATTACHED";
export const BIG_LOGO_ATTACHED = "BIG_LOGO_ATTACHED";
export const LOGO_REMOVED = "LOGO_REMOVED";
export const BIG_LOGO_REMOVED = "BIG_LOGO_REMOVED";

export const getCompanies =
  (
    term = null,
    page = DEFAULT_CURRENT_PAGE,
    perPage = DEFAULT_PER_PAGE,
    order = "id",
    orderDir = DEFAULT_ORDER_DIR
  ) =>
  async (dispatch) => {
    const accessToken = await getAccessTokenSafely();
    const filter = [];

    dispatch(startLoading());

    if (term) {
      const escapedTerm = escapeFilterValue(term);
      filter.push(`name=@${escapedTerm}`);
    }

    const params = {
      page,
      fields: "id,name,contact_email,member_level",
      relations: "none",
      per_page: perPage,
      access_token: accessToken
    };

    if (filter.length > 0) {
      params["filter[]"] = filter;
    }

    // order
    if (order != null && orderDir != null) {
      const orderDirSign = orderDir === 1 ? "+" : "-";
      params.order = `${orderDirSign}${order}`;
    }

    return getRequest(
      createAction(REQUEST_COMPANIES),
      createAction(RECEIVE_COMPANIES),
      `${window.API_BASE_URL}/api/v1/companies`,
      snackbarErrorHandler,
      { order, orderDir, page, perPage, term }
    )(params)(dispatch).finally(() => {
      dispatch(stopLoading());
    });
  };

export const getCompany = (companyId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "project_sponsorships,project_sponsorships.sponsored_project,project_sponsorships.supporting_companies"
  };

  return getRequest(
    null,
    createAction(RECEIVE_COMPANY),
    `${window.API_BASE_URL}/api/v1/companies/${companyId}`,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const deleteCompany = (companyId) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  return deleteRequest(
    null,
    createAction(COMPANY_DELETED)({ companyId }),
    `${window.API_BASE_URL}/api/v1/companies/${companyId}`,
    null,
    snackbarErrorHandler
  )(params)(dispatch).finally(() => {
    dispatch(stopLoading());
  });
};

export const resetCompanyForm = () => (dispatch) => {
  dispatch(createAction(RESET_COMPANY_FORM)({}));
};

export const saveCompany = (entity) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();
  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const normalizedEntity = normalizeEntity(entity);

  if (entity.id) {
    return putRequest(
      createAction(UPDATE_COMPANY),
      createAction(COMPANY_UPDATED),
      `${window.API_BASE_URL}/api/v1/companies/${entity.id}`,
      normalizedEntity,
      snackbarErrorHandler,
      entity
    )(params)(dispatch)
      .then(() => {
        dispatch(
          snackbarSuccessHandler({
            title: T.translate("general.success"),
            html: T.translate("edit_company.company_saved")
          })
        );
      })
      .finally(() => dispatch(stopLoading()));
  }

  return postRequest(
    createAction(UPDATE_COMPANY),
    createAction(COMPANY_ADDED),
    `${window.API_BASE_URL}/api/v1/companies`,
    normalizedEntity,
    snackbarErrorHandler,
    entity
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: T.translate("general.success"),
          html: T.translate("edit_company.company_created")
        })
      );
    })
    .finally(() => dispatch(stopLoading()));
};

export const attachLogo = (entity, file, picAttr) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const normalizedEntity = normalizeEntity(entity);

  const uploadFile = picAttr === "logo" ? uploadLogo : uploadBigLogo;

  if (entity.id) {
    return dispatch(uploadFile(entity, file));
  }

  return postRequest(
    createAction(UPDATE_COMPANY),
    createAction(COMPANY_ADDED),
    `${window.API_BASE_URL}/api/v1/companies`,
    normalizedEntity,
    snackbarErrorHandler,
    entity
  )(params)(dispatch).then((payload) => {
    dispatch(uploadFile(payload.response, file));
  });
};

const uploadLogo = (entity, file) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return postRequest(
    null,
    createAction(LOGO_ATTACHED),
    `${window.API_BASE_URL}/api/v1/companies/${entity.id}/logo`,
    file,
    snackbarErrorHandler,
    { pic: entity.pic }
  )(params)(dispatch).finally(() => dispatch(stopLoading()));
};

const uploadBigLogo = (entity, file) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return postRequest(
    null,
    createAction(BIG_LOGO_ATTACHED),
    `${window.API_BASE_URL}/api/v1/companies/${entity.id}/logo/big`,
    file,
    snackbarErrorHandler,
    { pic: entity.pic }
  )(params)(dispatch).finally(() => dispatch(stopLoading()));
};

const normalizeEntity = (entity) => {
  const normalizedEntity = { ...entity };

  // remove # from color hexa
  if (normalizedEntity.color.startsWith("#"))
    normalizedEntity.color = normalizedEntity.color.substr(1);

  if (entity.id > 0) {
    delete normalizedEntity.logo;
    delete normalizedEntity.big_logo;
  }

  return normalizedEntity;
};

export const removeLogo = (entity, picAttr) => async (dispatch) => {
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const endpoint =
    picAttr === "logo"
      ? `${window.API_BASE_URL}/api/v1/companies/${entity.id}/logo`
      : `${window.API_BASE_URL}/api/v1/companies/${entity.id}/logo/big`;

  const action = picAttr === "logo" ? LOGO_REMOVED : BIG_LOGO_REMOVED;

  return deleteRequest(
    null,
    createAction(action),
    endpoint,
    {},
    snackbarErrorHandler
  )(params)(dispatch).finally(() => dispatch(stopLoading()));
};

export const queryCompanies = debounce(async (input, callback) => {
  const accessToken = await getAccessTokenSafely();
  const endpoint = URI(`${window.API_BASE_URL}/api/v1/companies`);
  input = escapeFilterValue(input);
  endpoint.addQuery("access_token", accessToken);
  endpoint.addQuery("fields", "id,name");
  endpoint.addQuery("relations", "none");
  if (input) {
    endpoint.addQuery("filter", `name@@${input}`);
  }
  fetch(endpoint)
    .then(fetchResponseHandler)
    .then((json) => {
      const options = [...json.data];
      callback(options);
    })
    .catch(fetchErrorHandler);
}, DEBOUNCE_WAIT);
